import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import {
  AdminBanner,
  AdminButton,
  AdminCard,
  AdminField,
  AdminPageHead,
  AdminSpinner,
  Drawer,
  adminInputClass,
} from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";
import StoriesVideosManager from "@/components/admin/StoriesVideosManager";

/**
 * Homepage editor: every public homepage section, in page order, with its real fields,
 * show/hide, ordering and repeatable items. Saves into pages/home body_json.
 */
const SECTIONS = [
  {
    key: "hero",
    name: "Hero Banner",
    path: "hero",
    fields: [
      { k: "overline", label: "Small line above the headline" },
      { k: "heading_line_1", label: "Headline — first line" },
      { k: "heading_line_2", label: "Headline — second line (orange)" },
      { k: "subheading", label: "Short paragraph", type: "textarea" },
      { k: "primary_cta.label", label: "Main button label" },
      { k: "primary_cta.href", label: "Main button link" },
      { k: "secondary_cta.label", label: "Second button label" },
      { k: "secondary_cta.href", label: "Second button link" },
    ],
    list: { path: "hero.pillars", label: "Words beside the photo", simple: true },
    pageImage: true,
  },
  {
    key: "quick_links",
    name: "Four Cards (About / What We Do / Get Involved / Research)",
    list: {
      path: "quick_links",
      label: "Cards",
      fields: [
        { k: "title", label: "Card title" },
        { k: "text", label: "Card description" },
        { k: "href", label: "Link" },
        { k: "cta", label: "Hidden link description" },
        { k: "image_url", label: "Photo", type: "image" },
      ],
    },
  },
  {
    key: "gallery",
    name: "BKA In Action (photo gallery)",
    path: "gallery_section",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "heading_accent", label: "Heading — orange part" },
      { k: "item_cta", label: "Link text under each photo" },
      { k: "cta_label", label: "Button label" },
      { k: "cta_href", label: "Button link (leave empty to hide the button)" },
    ],
    list: {
      path: "gallery_section.items",
      label: "Photos (leave empty to show programmes and news automatically)",
      fields: [
        { k: "image_url", label: "Photo", type: "image" },
        { k: "title", label: "Caption" },
        { k: "label", label: "Small label" },
        { k: "href", label: "Link" },
      ],
    },
  },
  {
    key: "feature",
    name: "Medication Assistance Programme",
    path: "feature",
    fields: [
      { k: "eyebrow", label: "Small line above the heading" },
      { k: "title", label: "Heading" },
      { k: "text", label: "Paragraph", type: "textarea" },
      { k: "image_url", label: "Photo", type: "image" },
      { k: "cta.label", label: "Button label" },
      { k: "cta.href", label: "Button link" },
      { k: "note", label: "Small print", type: "textarea" },
    ],
    list: { path: "feature.medications", label: "Medication tags", simple: true },
  },
  {
    key: "stories",
    name: "Stories & Videos",
    path: "stories",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "intro", label: "Intro paragraph", type: "textarea" },
    ],
    note: "Videos and stories for this section are managed right here, and in Content → Videos.",
    manager: "home",
  },
  {
    key: "community",
    name: "BKA In The Community",
    path: "community",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "intro", label: "Intro paragraph", type: "textarea" },
    ],
    list: {
      path: "community.items",
      label: "Photo tiles",
      fields: [
        { k: "image_url", label: "Photo", type: "image" },
        { k: "caption", label: "Caption", type: "textarea" },
        { k: "alt", label: "Image description (accessibility)" },
      ],
    },
  },
  {
    key: "event",
    name: "Event Announcement Band",
    path: "event",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "title", label: "Event name" },
      { k: "text", label: "Description", type: "textarea" },
      { k: "patronage", label: "Patronage line" },
      { k: "date", label: "Date" },
      { k: "venue", label: "Venue" },
      { k: "times", label: "Times" },
      { k: "entertainment", label: "Entertainment" },
      { k: "contribution", label: "Tickets / contribution" },
      { k: "cta.label", label: "Button label" },
      { k: "cta.href", label: "Button link" },
    ],
  },
  {
    key: "emerald",
    name: "Emerald Honours Panel",
    path: "emerald_panel",
    fields: [
      { k: "eyebrow", label: "Small line above the heading" },
      { k: "title", label: "Heading" },
      { k: "text", label: "Paragraph", type: "textarea" },
      { k: "image_url", label: "Photo", type: "image" },
      { k: "primary_cta.label", label: "Main button label" },
      { k: "primary_cta.href", label: "Main button link" },
      { k: "secondary_cta.label", label: "Second button label" },
      { k: "secondary_cta.href", label: "Second button link" },
    ],
  },
  {
    key: "testimonials",
    name: "What People Are Saying",
    path: "testimonials_section",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "empty_text", label: "Text shown when there are no quotes yet", type: "textarea" },
    ],
    note: "Quotes come from Content → Testimonials. Video testimonials are added here, or in Content → Videos.",
    manager: "testimonials",
  },
  {
    key: "posts",
    name: "Latest News",
    path: "posts_section",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "cta_label", label: "Link text" },
      { k: "cta_href", label: "Link" },
    ],
    note: "The news items come from Content → Posts.",
  },
  {
    key: "closing",
    name: "Closing Band",
    path: "closing_band",
    fields: [
      { k: "overline", label: "Small line above the heading" },
      { k: "heading", label: "Heading" },
      { k: "heading_accent", label: "Heading — underlined part" },
      { k: "primary_cta.label", label: "Main button label" },
      { k: "primary_cta.href", label: "Main button link" },
      { k: "secondary_cta.label", label: "Second button label" },
      { k: "secondary_cta.href", label: "Second button link" },
    ],
  },
];

const getAt = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

const setAt = (obj, path, value) => {
  const keys = path.split(".");
  const next = { ...obj };
  let cursor = next;
  keys.forEach((key, i) => {
    if (i === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = Array.isArray(cursor[key]) ? [...cursor[key]] : { ...(cursor[key] || {}) };
    cursor = cursor[key];
  });
  return next;
};

export function Homepage() {
  const [page, setPage] = useState(null);
  const [body, setBody] = useState({});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState({ status: "idle", message: "" });

  const load = useCallback(() => {
    api
      .get("/admin/content/pages", { params: { _: Date.now() } })
      .then(async (r) => {
        const home = (r.data || []).find((p) => p.slug === "home");
        const { data } = await api.get(`/admin/content/pages/${home.id}`);
        setPage(data);
        setBody(data.body_json || {});
      })
      .catch((e) => setBanner({ status: "error", message: apiError(e) }));
  }, []);

  useEffect(load, [load]);

  const persist = async (nextBody, successMessage, extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/content/pages/${page.id}`, {
        title: page.title,
        slug: page.slug,
        status: page.status || "published",
        page_type: page.page_type,
        seo_title: page.seo_title,
        seo_description: page.seo_description,
        body: page.body,
        body_json: nextBody,
        hero_image_url: extra.hero_image_url ?? page.hero_image_url,
      });
      setPage(data);
      setBody(data.body_json || nextBody);
      setBanner({ status: "ok", message: successMessage || "Saved. The homepage is updated." });
      return true;
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const metaFor = (key) => body.sections_meta?.[key] || {};
  const orderOf = (key) =>
    Number(metaFor(key).order ?? (SECTIONS.findIndex((s) => s.key === key) + 1) * 10);

  const sortedSections = [...SECTIONS].sort((a, b) => orderOf(a.key) - orderOf(b.key));

  const toggleSection = (key) => {
    const enabled = metaFor(key).enabled !== false;
    const next = setAt(body, `sections_meta.${key}`, { ...metaFor(key), enabled: !enabled, order: orderOf(key) });
    persist(next, !enabled ? "Section hidden from the homepage." : "Section is visible again.");
  };

  const moveSection = (key, dir) => {
    const index = sortedSections.findIndex((s) => s.key === key);
    const target = index + dir;
    if (target < 0 || target >= sortedSections.length) return;
    const other = sortedSections[target];
    let next = setAt(body, `sections_meta.${key}`, { ...metaFor(key), order: orderOf(other.key), enabled: metaFor(key).enabled !== false });
    next = setAt(next, `sections_meta.${other.key}`, { ...metaFor(other.key), order: orderOf(key), enabled: metaFor(other.key).enabled !== false });
    persist(next, "Section order updated.");
  };

  const openEditor = (section) => {
    setEditing({ section, draft: JSON.parse(JSON.stringify(body)), heroImage: page.hero_image_url });
  };

  const saveEditor = async () => {
    const ok = await persist(editing.draft, "Saved. The homepage is updated.", {
      hero_image_url: editing.heroImage,
    });
    if (ok) setEditing(null);
  };

  const draftSet = (path, value) => setEditing((e) => ({ ...e, draft: setAt(e.draft, path, value) }));

  const listOf = (draft, path) => {
    const value = getAt(draft, path);
    return Array.isArray(value) ? value : [];
  };

  const renderField = (field, basePath) => {
    const path = basePath ? `${basePath}.${field.k}` : field.k;
    const value = getAt(editing.draft, path) ?? "";
    if (field.type === "image") {
      return (
        <AdminField key={path} label={field.label}>
          <MediaPicker
            kind="images"
            label=""
            value={value}
            onChange={(url) => draftSet(path, url)}
            buttonLabel="Choose or Upload Picture"
            testId={`home-cms-${path.replace(/\./g, "-")}`}
          />
        </AdminField>
      );
    }
    if (field.type === "textarea") {
      return (
        <AdminField key={path} label={field.label}>
          <textarea
            rows={4}
            value={value}
            onChange={(e) => draftSet(path, e.target.value)}
            className={adminInputClass}
            data-testid={`home-cms-${path.replace(/\./g, "-")}`}
          />
        </AdminField>
      );
    }
    return (
      <AdminField key={path} label={field.label}>
        <input
          value={value}
          onChange={(e) => draftSet(path, e.target.value)}
          className={adminInputClass}
          data-testid={`home-cms-${path.replace(/\./g, "-")}`}
        />
      </AdminField>
    );
  };

  const renderList = (list) => {
    const items = listOf(editing.draft, list.path);
    const addItem = () =>
      draftSet(list.path, [...items, list.simple ? "" : Object.fromEntries((list.fields || []).map((f) => [f.k, ""]))]);
    const removeItem = (i) => {
      if (!window.confirm("Remove this item?")) return;
      draftSet(list.path, items.filter((_, index) => index !== i));
    };
    const moveItem = (i, dir) => {
      const target = i + dir;
      if (target < 0 || target >= items.length) return;
      const next = [...items];
      [next[i], next[target]] = [next[target], next[i]];
      draftSet(list.path, next);
    };

    return (
      <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-extrabold text-bka-navy">{list.label}</p>
          <AdminButton variant="ghost" className="px-3 py-1.5 text-xs" testId="home-cms-list-add" onClick={addItem}>
            <Plus className="h-3.5 w-3.5" /> Add
          </AdminButton>
        </div>

        {items.length === 0 && <p className="text-xs text-slate-500">Nothing here yet.</p>}

        {items.map((item, i) => (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`home-cms-list-item-${i}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className="text-xs font-extrabold uppercase tracking-wide text-slate-400">Item {i + 1}</span>
              <div className="flex gap-1.5">
                {!list.simple && (
                  <button
                    type="button"
                    onClick={() => draftSet(`${list.path}.${i}.enabled`, item.enabled === false)}
                    className="rounded-lg border border-slate-200 px-2 py-1 text-[11px] font-bold text-slate-500 hover:border-bka-emerald hover:text-bka-emerald"
                    data-testid={`home-cms-list-toggle-${i}`}
                  >
                    {item.enabled === false ? "Hidden" : "Visible"}
                  </button>
                )}
                <button type="button" onClick={() => moveItem(i, -1)} aria-label="Move up" className="rounded-lg border border-slate-200 p-1 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald" data-testid={`home-cms-list-up-${i}`}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => moveItem(i, 1)} aria-label="Move down" className="rounded-lg border border-slate-200 p-1 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald" data-testid={`home-cms-list-down-${i}`}>
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => removeItem(i)} aria-label="Remove item" className="rounded-lg border border-slate-200 p-1 text-slate-500 hover:border-red-300 hover:text-red-600" data-testid={`home-cms-list-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {list.simple ? (
              <input
                value={item || ""}
                onChange={(e) => draftSet(`${list.path}.${i}`, e.target.value)}
                className={adminInputClass}
                data-testid={`home-cms-list-value-${i}`}
              />
            ) : (
              <div className="space-y-3">{(list.fields || []).map((f) => renderField(f, `${list.path}.${i}`))}</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (!page) {
    return (
      <div>
        <AdminPageHead title="Homepage" subtitle="Every section of the public homepage." />
        <AdminCard><AdminSpinner /></AdminCard>
      </div>
    );
  }

  return (
    <div data-testid="cms-homepage">
      <Seo title="Homepage | Bahamas Kidney Association CMS" noindex />
      <AdminPageHead
        title="Homepage"
        subtitle="Every section of the public homepage, in the order visitors see them. Edit the words, pictures and buttons, hide a section or change its position."
      />

      <AdminBanner status={banner.status} message={banner.message} onDismiss={() => setBanner({ status: "idle", message: "" })} />

      <div className="space-y-3" data-testid="cms-homepage-sections">
        {sortedSections.map((section, index) => {
          const enabled = metaFor(section.key).enabled !== false;
          return (
            <div
              key={section.key}
              className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              data-testid={`cms-homepage-section-${section.key}`}
            >
              <div className="min-w-[220px]">
                <p className="text-xs font-extrabold uppercase tracking-wide text-slate-400">Position {index + 1}</p>
                <p className="mt-1 font-display text-lg font-black text-bka-navy">{section.name}</p>
                <p className="mt-0.5 text-xs font-semibold text-slate-500">
                  {enabled ? "Showing on the homepage" : "Hidden from the homepage"}
                  {section.note ? ` · ${section.note}` : ""}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                <AdminButton variant="emerald" className="px-4 py-2 text-xs" testId={`cms-homepage-edit-${section.key}`} onClick={() => openEditor(section)}>
                  Edit
                </AdminButton>
                <AdminButton variant="ghost" className="px-3 py-2 text-xs" testId={`cms-homepage-toggle-${section.key}`} onClick={() => toggleSection(section.key)} disabled={saving}>
                  {enabled ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Show</>}
                </AdminButton>
                <button type="button" onClick={() => moveSection(section.key, -1)} disabled={saving || index === 0} aria-label="Move section up" data-testid={`cms-homepage-up-${section.key}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald disabled:opacity-35">
                  <ArrowUp className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => moveSection(section.key, 1)} disabled={saving || index === sortedSections.length - 1} aria-label="Move section down" data-testid={`cms-homepage-down-${section.key}`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald disabled:opacity-35">
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Drawer open={Boolean(editing)} onClose={() => setEditing(null)} title={editing?.section.name || ""} testId="cms-homepage-drawer">
        {editing && (
          <div className="space-y-5">
            {editing.section.note && (
              <p className="rounded-xl bg-slate-50 p-3 text-xs font-semibold text-slate-600">{editing.section.note}</p>
            )}

            {editing.section.pageImage && (
              <AdminField label="Banner photograph">
                <MediaPicker
                  kind="images"
                  label=""
                  value={editing.heroImage}
                  onChange={(url) => setEditing((e) => ({ ...e, heroImage: url }))}
                  buttonLabel="Choose or Upload Banner Photo"
                  testId="home-cms-hero-image"
                />
              </AdminField>
            )}

            {(editing.section.fields || []).map((f) => renderField(f, editing.section.path))}

            {editing.section.list && renderList(editing.section.list)}

            {editing.section.manager && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <StoriesVideosManager placement={editing.section.manager} lockPlacement showLibraryLink />
              </div>
            )}

            <AdminButton variant="emerald" testId="cms-homepage-save" onClick={saveEditor} disabled={saving}>
              {saving ? "Saving…" : "Save & Publish"}
            </AdminButton>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default Homepage;
